import React, { useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import Sidebar from "../components/Sidebar"

const JURISDICTIONS = [
  { key: "US (FED)", label: "US (FED)" },
  { key: "US (STATE)", label: "US (STATE)" },
  { key: "UK", label: "UK" },
  { key: "EU", label: "EU" },
]

function toNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

const StartAuditLegacy = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // If user is in /app/*, keep navigation inside /app/*
  const routePrefix = useMemo(
    () => (location.pathname.startsWith("/app") ? "/app" : ""),
    [location.pathname]
  )

  const [formData, setFormData] = useState({
    clientName: "",
    industry: "",
    niche: "",
    annualTurnover: "5000000",
    companySize: "ENTERPRISE",
    userImpact: 100,
    jurisdictions: ["EU"], // multi-select
  })

  const hasFed = formData.jurisdictions.includes("US (FED)")
  const hasState = formData.jurisdictions.includes("US (STATE)")

  const toggleJurisdiction = (j) => {
    setFormData((prev) => {
      const selected = new Set(prev.jurisdictions)

      // If already selected, deselect it
      if (selected.has(j)) {
        selected.delete(j)
        // Ensure at least one jurisdiction remains selected
        if (selected.size === 0) selected.add("EU")
        return { ...prev, jurisdictions: Array.from(selected) }
      }

      // If selecting FED, remove STATE. If selecting STATE, remove FED.
      if (j === "US (FED)") selected.delete("US (STATE)")
      if (j === "US (STATE)") selected.delete("US (FED)")

      selected.add(j)
      return { ...prev, jurisdictions: Array.from(selected) }
    })
  }

  const handleStart = (e) => {
    e.preventDefault()

    const auditConfig = {
      // Optional business identifier
      clientName: String(formData.clientName || "").trim(),

      // REQUIRED for your product flow
      industry: String(formData.industry || "").trim(),
      niche: String(formData.niche || "").trim(),

      // REQUIRED core metrics
      jurisdictions: formData.jurisdictions,
      annualTurnover: toNumber(formData.annualTurnover, 0),
      userImpact: toNumber(formData.userImpact, 0),

      // Optional (keep if you use it later)
      companySize: formData.companySize,
    }

    localStorage.setItem("aifine_config", JSON.stringify(auditConfig))

    // ✅ Step 2: go to assessment inside the same namespace
    navigate(`${routePrefix}/assessment`, { replace: true })
  }

  const isStartDisabled =
    !formData.industry.trim() ||
    !formData.niche.trim() ||
    !formData.jurisdictions.length ||
    toNumber(formData.annualTurnover, 0) <= 0 ||
    toNumber(formData.userImpact, 0) <= 0

  return (
    <div className="flex h-screen bg-slate-50 font-inter text-blue-900 overflow-hidden">
      <Sidebar />
      <main className="flex-1 flex items-center justify-center p-12">
        <div className="max-w-4xl w-full bg-white p-16 rounded-[4rem] shadow-2xl shadow-blue-900/5 text-center border border-slate-100">
          <header className="mb-12">
            <h1 className="text-4xl font-black uppercase italic tracking-tighter leading-none mb-2">
              START <span className="text-blue-600">DETAILED ASSESSMENT</span>
            </h1>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-300">
              AI Fine Protector™ • Configuration
            </p>
          </header>

          <form onSubmit={handleStart} className="space-y-10 text-left">
            {/* Client name (optional but useful) */}
            <div>
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3 block ml-4 italic">
                Client Entity Name (optional)
              </label>
              <input
                className="w-full bg-slate-50 border-none p-6 rounded-2xl text-lg font-bold placeholder:text-slate-200 outline-blue-600 transition-all"
                placeholder="Legal Entity Name..."
                value={formData.clientName}
                onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
              />
            </div>

            {/* REQUIRED: Industry + Niche */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3 block ml-4 italic">
                  Industry (required)
                </label>
                <input
                  required
                  className="w-full bg-slate-50 border-none p-6 rounded-2xl text-lg font-bold placeholder:text-slate-200 outline-blue-600 transition-all"
                  placeholder="e.g., Healthcare, Financial Services..."
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                />
              </div>

              <div>
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3 block ml-4 italic">
                  Niche (required)
                </label>
                <input
                  required
                  className="w-full bg-slate-50 border-none p-6 rounded-2xl text-lg font-bold placeholder:text-slate-200 outline-blue-600 transition-all"
                  placeholder="e.g., Medical Billing, Lending, HR Screening..."
                  value={formData.niche}
                  onChange={(e) => setFormData({ ...formData, niche: e.target.value })}
                />
              </div>
            </div>

            {/* Revenue + Company size */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3 block ml-4 italic">
                  Gross Annual Revenue (USD, estimate)
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  className="w-full bg-slate-50 border-none p-6 rounded-2xl text-xl font-black italic outline-blue-600"
                  value={formData.annualTurnover}
                  onChange={(e) => setFormData({ ...formData, annualTurnover: e.target.value })}
                />
              </div>

              <div>
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3 block ml-4 italic">
                  Company Size
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {["SME", "ENTERPRISE"].map((size) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => setFormData({ ...formData, companySize: size })}
                      className={`py-5 rounded-2xl font-black text-[10px] tracking-widest border-2 transition-all ${
                        formData.companySize === size
                          ? "border-blue-600 text-blue-600 bg-blue-50 shadow-inner"
                          : "border-slate-100 text-slate-300 hover:border-slate-200"
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Monthly impact */}
            <div>
              <div className="flex justify-between items-end mb-4 px-4">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 italic">
                  Humans Impacted Per Month (approx.)
                </label>
                <span className="text-2xl font-black italic text-blue-600 leading-none">
                  {toNumber(formData.userImpact).toLocaleString()}
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="1000000"
                step="1000"
                className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                value={formData.userImpact}
                onChange={(e) => setFormData({ ...formData, userImpact: e.target.value })}
              />
            </div>

            {/* Jurisdictions (multi-select with XOR rule) */}
            <div>
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3 block ml-4 italic">
                Jurisdictions (select all that apply)
              </label>

              <div className="mb-3 px-4 text-[10px] text-slate-400">
                Rule: You can select any combination <span className="font-black">except</span>{" "}
                <span className="font-black">US (FED)</span> and{" "}
                <span className="font-black">US (STATE)</span> together.
              </div>

              <div className="grid grid-cols-4 gap-4">
                {JURISDICTIONS.map((j) => {
                  const isSelected = formData.jurisdictions.includes(j.key)
                  const isDisabled =
                    (j.key === "US (FED)" && hasState) || (j.key === "US (STATE)" && hasFed)

                  return (
                    <button
                      key={j.key}
                      type="button"
                      onClick={() => !isDisabled && toggleJurisdiction(j.key)}
                      className={`py-4 rounded-xl font-black text-[8px] tracking-widest border-2 transition-all ${
                        isDisabled
                          ? "border-slate-100 text-slate-200 bg-slate-50 cursor-not-allowed"
                          : isSelected
                          ? "border-blue-600 text-blue-600 bg-blue-50"
                          : "border-slate-100 text-slate-300 hover:border-slate-200"
                      }`}
                      aria-disabled={isDisabled}
                    >
                      {j.label}
                    </button>
                  )
                })}
              </div>

              <div className="mt-3 px-4 text-[10px] text-slate-400">
                Selected:{" "}
                <span className="font-black text-slate-500">
                  {formData.jurisdictions.join(", ")}
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={isStartDisabled}
              className={`w-full py-8 rounded-[2.5rem] font-black uppercase italic tracking-widest text-lg shadow-2xl transition-all ${
                isStartDisabled
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "bg-blue-600 text-white shadow-blue-200 hover:scale-[1.01] active:scale-95"
              }`}
            >
              INITIALIZE DETAILED ASSESSMENT
            </button>
          </form>
        </div>
      </main>
    </div>
  )
}

export default StartAuditLegacy
